import { CommonHelper } from 'src/utils/commonHelper';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { alpha } from '@mui/material/styles';
import { Alert, Box, Button, Card, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import HttpServices from 'src/services/httpService';
import { paymentModuleUrl } from 'src/services/urlService';
import PaymentDialogComponent from 'src/pages/payment/payment-dialog.component';

const displayName = (name) =>
  String(name || '').trim().replace(/_/g, ' ').replace(/\s+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

export default function DefaultPlansComponent({ currentPlanDetails = null }) {
  useTranslation();
  const billingTimezone = CommonHelper.GetBrandTimeZone();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [buying, setBuying] = useState(null);
  const [payment, setPayment] = useState(null);
  const hasLifetime = Boolean(currentPlanDetails?.is_appsumo_user ||
    `${currentPlanDetails?.default_plan_name || ''} ${currentPlanDetails?.paid_plan_name || ''}`.toLowerCase().includes('lifetime'));

  const buyPlan = (plan) => {
    setBuying(plan.id);
    setError('');
    HttpServices.Post({
      url: paymentModuleUrl.lifetimePayment,
      body: { default_plan_id: plan.id, timezone: billingTimezone },
      successCallback: (data) => {
        setBuying(null);
        if (!data?.payment_intent) {
          setError(i18n.t('plan_access.payment_error'));
          return;
        }
        setPayment({ clientSecret: data.payment_intent, amount: Number(data.converted_amount) / 100, currencySymbol: plan.currency_symbol, name: i18n.t(`plan_access.${({ 1: 'free', 2: 'team', 3: 'business', 4: 'professional' })[plan.id]}`, { defaultValue: displayName(plan.name) }) });
      },
      failureCallback: (message) => {
        setBuying(null);
        setError(message || i18n.t('plan_access.payment_error'));
      },
    });
  };

  useEffect(() => {
    HttpServices.Get({
      url: `${paymentModuleUrl.getDefaultPlans}?timezone=${encodeURIComponent(billingTimezone || '')}`,
      successCallback: (data) => {
        setPlans(Array.isArray(data) ? data : []);
        setLoading(false);
      },
      failureCallback: (message) => {
        setError(message || i18n.t('plan_access.load_error'));
        setLoading(false);
      },
    });
  }, [billingTimezone]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error && !plans.length) return <Alert severity="error">{error}</Alert>;
  if (!plans.length) return <Alert severity="info">{i18n.t('plan_access.empty')}</Alert>;

  return (
    <Stack spacing={3} sx={{ width: '100%', minWidth: 0, py: 2 }}>
      <Box>
        <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>{i18n.t('plan_access.heading')}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, mb: 1 }}>{i18n.t('plan_access.title')}</Typography>
        <Typography color="text.secondary">{i18n.t('plan_access.description')}</Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 2.5 }}>
        {plans.map((plan) => {
          const isCurrent = Number(currentPlanDetails?.default_plan_id) === plan.id;
          const isBusiness = plan.id === 3;
          const isProfessional = ['professional', 'pro'].includes(String(plan.plan_type || '').trim().toLowerCase());
          const name = i18n.t(`plan_access.${({ 1: 'free', 2: 'team', 3: 'business', 4: 'professional' })[plan.id]}`, { defaultValue: displayName(plan.name) });
          const features = [
            [plan.no_of_brands, 'brand'],
            [plan.no_of_users, 'user'],
            [plan.no_of_social_accounts, 'social_account'],
          ];
          return (
            <Card key={plan.id} sx={{
              p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', minWidth: 0,
              boxShadow: 'none', border: '1px solid', borderColor: isCurrent ? 'primary.main' : 'divider',
              background: (theme) => isBusiness ? `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.08)}, ${theme.palette.background.paper} 65%)` : theme.palette.background.paper,
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minHeight: 28, mb: 2 }}>
                <Typography variant="h6">{name}</Typography>
                {isCurrent && <Chip size="small" color="primary" variant="outlined" label={i18n.t('plan_access.current')} />}
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1 }}>{plan.currency_symbol || '$'}{Number(plan.price_amount).toLocaleString(plan.currency_code === 'inr' ? 'en-IN' : 'en-US')}</Typography>
                <Typography variant="body2" color="text.secondary">{String(plan.currency_code || 'usd').toUpperCase()}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>{plan.id === 1 ? i18n.t('plan_access.free_start') : i18n.t('plan_access.once')}</Typography>
              <Chip size="small" label={i18n.t(isProfessional ? 'plan_access.professional_features' : 'plan_access.basic_features')} sx={{ alignSelf: 'flex-start', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }} />
              <Divider sx={{ my: 2.5 }} />
              <Stack spacing={1.75} sx={{ flex: 1, mb: 3 }}>
                {features.map(([count, label]) => (
                  <Stack direction="row" spacing={1.25} alignItems="center" key={label}>
                    <CheckCircleOutlineRoundedIcon sx={{ color: 'primary.main', fontSize: 19 }} />
                    <Typography variant="body2"><Box component="span" sx={{ fontWeight: 700 }}>{count}</Box> {i18n.t(`plan_access.${label}${Number(count) === 1 ? '' : 's'}`)}</Typography>
                  </Stack>
                ))}
                <Typography variant="caption" color="text.secondary">
                  {isProfessional ? i18n.t('plan_access.channels') : i18n.t('plan_access.channels_basic')}
                </Typography>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleOutlineRoundedIcon sx={{ color: plan.id === 1 ? 'text.disabled' : 'primary.main', fontSize: 19 }} />
                  <Typography variant="body2" color={plan.id === 1 ? 'text.secondary' : 'text.primary'}>
                    {plan.id === 1 ? i18n.t('plan_access.ai_excluded') : i18n.t('plan_access.ai_included')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleOutlineRoundedIcon sx={{ color: isProfessional ? 'primary.main' : 'text.disabled', fontSize: 19 }} />
                  <Typography variant="body2" color={isProfessional ? 'text.primary' : 'text.secondary'}>
                    {isProfessional ? i18n.t('plan_access.tickets_included') : i18n.t('plan_access.tickets_excluded')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleOutlineRoundedIcon sx={{ color: plan.has_cname ? 'primary.main' : 'text.disabled', fontSize: 19 }} />
                  <Typography variant="body2" color={plan.has_cname ? 'text.primary' : 'text.secondary'}>{plan.has_cname ? i18n.t('plan_access.domain_included') : i18n.t('plan_access.domain_excluded')}</Typography>
                </Stack>
              </Stack>
              <Button fullWidth size="large" variant={plan.id === 1 || isCurrent ? 'outlined' : 'contained'} disableElevation
                onClick={() => buyPlan(plan)} sx={{ borderRadius: 1.5, py: 1.25 }}
                disabled={plan.id === 1 || hasLifetime || buying !== null || !currentPlanDetails}>
                {isCurrent ? i18n.t('plan_access.current') : plan.id === 1 ? i18n.t('plan_access.free_plan') : hasLifetime ? i18n.t('plan_access.active') : buying === plan.id ? i18n.t('plan_access.starting') : i18n.t('plan_access.choose', { name })}
              </Button>
            </Card>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>{i18n.t('plan_access.footer', { currency: String(plans[0]?.currency_code || 'usd').toUpperCase() })}</Typography>
      {payment && <PaymentDialogComponent clientSecret={payment.clientSecret}
        amount={payment.amount} planName={payment.name} currencySymbol={payment.currencySymbol || '$'}
        handleClose={() => setPayment(null)} />}
    </Stack>
  );
}
